import { useEffect, useRef, useState } from 'react';
import { IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { sendSignatureToDesktop } from '../../../../../lib/utils/signaturePeerRelay';
import SignaturePadCanvas from './SignaturePadCanvas';
import { PEN_COLORS, STROKE_WIDTHS } from './signaturePadImageEngine';

export default function SignaturePadMobileView({ sessionSlug }) {
    const { t } = useTranslation();
    const padRef = useRef(null);
    const sendOnceRef = useRef(false);

    const [hasStroke, setHasStroke] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [linkReused, setLinkReused] = useState(false);
    const reuseWarnedRef = useRef(false);

    useEffect(() => {
        document.body.classList.add('signature-mobile-only');
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.classList.remove('signature-mobile-only');
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        if (!sessionSlug || typeof sessionStorage === 'undefined') return;
        if (sessionStorage.getItem(`sig_m_done_${sessionSlug}`) !== '1') return;

        setLinkReused(true);
        setSent(true);

        if (reuseWarnedRef.current) return;
        reuseWarnedRef.current = true;
        notifications.show({
            title: t('signaturePadImage.qr.mobile.linkUsedTitle'),
            message: t('signaturePadImage.qr.mobile.linkUsedMessage'),
            color: 'orange',
        });
    }, [sessionSlug, t]);

    const handleSend = async () => {
        if (!sessionSlug) {
            notifications.show({
                title: t('common.error'),
                message: t('signaturePadImage.qr.mobile.noSession'),
                color: 'red',
            });
            return;
        }

        if (
            typeof sessionStorage !== 'undefined'
            && sessionStorage.getItem(`sig_m_done_${sessionSlug}`) === '1'
        ) {
            setLinkReused(true);
            setSent(true);
            notifications.show({
                title: t('signaturePadImage.qr.mobile.linkUsedTitle'),
                message: t('signaturePadImage.qr.mobile.cannotResend'),
                color: 'orange',
            });
            return;
        }

        if (sendOnceRef.current || sent) return;

        const canvas = padRef.current?.getCanvas();
        if (!canvas || !hasStroke) {
            notifications.show({
                title: t('common.error'),
                message: t('signaturePadImage.qr.mobile.drawFirst'),
                color: 'orange',
            });
            return;
        }

        setSending(true);
        sendOnceRef.current = true;

        try {
            const blob = await new Promise((resolve, reject) => {
                canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('blob'))), 'image/png', 1);
            });
            const file = new File([blob], 'signature.png', { type: 'image/png' });
            await sendSignatureToDesktop(sessionSlug, [file]);

            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(`sig_m_done_${sessionSlug}`, '1');
            }
            setSent(true);
            notifications.show({
                title: t('common.success'),
                message: t('signaturePadImage.qr.mobile.sent'),
                color: 'green',
            });
        } catch (err) {
            sendOnceRef.current = false;
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('signaturePadImage.qr.mobile.sendFailed'),
                color: 'red',
            });
        } finally {
            setSending(false);
        }
    };

    if (!sessionSlug) {
        return (
            <div className="flex min-h-[100dvh] items-center justify-center p-4">
                <p className="text-center text-[14px] text-red-500">
                    {t('signaturePadImage.qr.mobile.invalidLink')}
                </p>
            </div>
        );
    }

    if (sent) {
        if (linkReused) {
            return (
                <div className="flex min-h-[100dvh] items-center justify-center p-6">
                    <div className="max-w-md space-y-4 text-center">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
                            <IconAlertTriangle size={32} stroke={1.8} />
                        </div>
                        <h1 className="text-lg font-bold text-orange-700 dark:text-orange-300">
                            {t('signaturePadImage.qr.mobile.reusedTitle')}
                        </h1>
                        <p className="text-[13px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                            {t('signaturePadImage.qr.mobile.reusedBody')}
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div className="flex min-h-[100dvh] items-center justify-center p-6">
                <div className="max-w-md space-y-4 text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                        <IconCheck size={32} stroke={2} />
                    </div>
                    <h1 className="text-lg font-bold text-[#1C1815] dark:text-zinc-100">
                        {t('signaturePadImage.qr.mobile.successTitle')}
                    </h1>
                    <p className="text-[13px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                        {t('signaturePadImage.qr.mobile.successBody')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-white dark:bg-zinc-900">
            <div className="flex shrink-0 items-center justify-between border-b border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                <p className="text-[14px] font-bold text-[#1C1815] dark:text-zinc-100">
                    {t('signaturePadImage.padTitle')}
                </p>
                <button
                    type="button"
                    onClick={() => padRef.current?.clear()}
                    className="rounded-lg border border-[#ECE8E4] px-3 py-1.5 text-[12px] font-semibold text-[#6B635C] dark:border-zinc-700 dark:text-zinc-300"
                >
                    {t('signaturePadImage.clear')}
                </button>
            </div>

            <p className="shrink-0 px-4 py-2 text-center text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                {t('signaturePadImage.qr.mobile.rotateHint')}
            </p>

            <div className="relative min-h-0 flex-1 px-3 pb-3">
                <div className="absolute inset-0 px-3 pb-3">
                    <SignaturePadCanvas
                        ref={padRef}
                        penColor={PEN_COLORS[0]}
                        strokeWidth={STROKE_WIDTHS[1]}
                        backgroundMode="white"
                        emptyHint={t('signaturePadImage.qr.mobile.signHere')}
                        onStrokeChange={setHasStroke}
                    />
                </div>
            </div>

            <div className="shrink-0 border-t border-[#ECE8E4] p-4 dark:border-zinc-700">
                <button
                    type="button"
                    onClick={handleSend}
                    disabled={!hasStroke || sending}
                    className="w-full rounded-xl bg-violet-600 px-4 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {sending ? t('signaturePadImage.qr.mobile.sending') : t('signaturePadImage.qr.mobile.send')}
                </button>
            </div>
        </div>
    );
}
